import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BriefcaseBusiness,
  Building2,
  Calculator,
  CalendarX2,
  ChevronDown,
  ClipboardList,
  ClipboardPlus,
  Clock3,
  ListChecks,
  Mail,
  FileBarChart,
  FileText,
  FolderOpen,
  HeartPulse,
  LayoutDashboard,
  LockKeyhole,
  Repeat2,
  ShieldCheck,
  TrendingUp,
  Eye,
  UserCog,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import logoWhite from "@/assets/tagforce-logo-white.png";
import { useAuth } from "@/context/AuthContext";
import { canAccessRoute } from "@/lib/accessPolicy";
import { closeMobileNav, isMobileNavOpen } from "@/lib/mobileNav";

const groups = [
  { label: "Home", items: [{ label: "Dashboard", path: "/", icon: LayoutDashboard }] },
  {
    label: "Workforce",
    items: [
      { label: "Master staffing board", path: "/master-staffing-board", icon: ClipboardList },
      { label: "Providers", path: "/providers", icon: HeartPulse },
      { label: "Providers & assignments", path: "/client-assignments", icon: BriefcaseBusiness },
      { label: "My caseload", path: "/provider-portal", icon: UserRound, roles: ["system_administrator"] },
      { label: "Provider portal", path: "/provider-portal", icon: UserRound, roles: ["provider"] },
      { label: "Assignments", path: "/assignments", icon: BriefcaseBusiness },
      { label: "Progress notes", path: "/progress-notes", icon: ClipboardPlus, roles: ["system_administrator", "executive", "hr", "operations"] },
    ],
  },
  {
    label: "Clients",
    items: [
      { label: "Clients", path: "/clients", icon: Building2 },
      { label: "Client logins", path: "/client-accounts", icon: UserCog },
      { label: "Client view preview", path: "/client-portal-preview", icon: Eye, roles: ["system_administrator"] },
      { label: "Evaluation pipeline", path: "/evaluation-pipeline", icon: FileText },
      { label: "Client change requests", path: "/change-review", icon: Repeat2 },
      { label: "Evaluation requests", path: "/client-evaluation-request", icon: ClipboardPlus },
      { label: "Documents", path: "/client-documents", icon: FolderOpen },
    ],
  },
  {
    label: "Compliance",
    items: [
      { label: "Credentials", path: "/credential-intelligence", icon: ShieldCheck },
      { label: "Client requirements", path: "/compliance", icon: ShieldCheck },
      { label: "Supervision verification", path: "/supervision-verification", icon: ShieldCheck, roles: ["hr", "system_administrator"] },
      { label: "Contract library", path: "/contracts", icon: LockKeyhole },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Connecteam", path: "/connecteam", icon: Clock3 },
      { label: "School closures", path: "/closures", icon: CalendarX2 },
      { label: "Tasks", path: "/tasks", icon: ListChecks },
      { label: "Notification log", path: "/notification-log", icon: Mail },
    ],
  },
  {
    label: "Finance",
    items: [
      { label: "Pricing tools", path: "/pricing-tools", icon: Calculator },
      { label: "Evaluation billing", path: "/evaluation-billing", icon: FileBarChart },
      { label: "Projections", path: "/projections", icon: TrendingUp },
      { label: "Reports", path: "/reports", icon: FileBarChart },
    ],
  },
  { label: "Administration", items: [{ label: "Team directory", path: "/team", icon: UsersRound }] },
];

const STORAGE_KEY = "tagforce.nav.collapsed";

const FOCUSABLE = "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

function permitted(item, role) {
  return item.roles ? item.roles.includes(role) : canAccessRoute(item.path, role);
}

function readCollapsed() {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

export { closeMobileNav };

export default function AppSidebar({ activeItem = "Dashboard" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const asideRef = useRef(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const activeKey = activeItem.toLowerCase();
  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({ ...group, items: group.items.filter((item) => permitted(item, user?.role)) }))
        .filter((group) => group.items.length),
    [user?.role],
  );
  const activeGroup = visibleGroups.find((group) => group.items.some((item) => item.label.toLowerCase() === activeKey))?.label;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && isMobileNavOpen()) closeMobileNav();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      closeMobileNav({ restoreFocus: false });
    };
  }, []);

  const trapFocus = (event) => {
    if (event.key !== "Tab" || !isMobileNavOpen() || !asideRef.current) return;
    const focusable = [...asideRef.current.querySelectorAll(FOCUSABLE)].filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const go = (path) => {
    closeMobileNav({ restoreFocus: false });
    navigate(path);
  };

  function toggleGroup(label) {
    setCollapsed((current) => {
      const next = new Set(current);
      next.has(label) ? next.delete(label) : next.add(label);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  return (
    <>
      <div className="sidebar-backdrop" data-testid="sidebar-backdrop" onClick={() => closeMobileNav()} aria-hidden="true" />
      <aside className="app-sidebar" data-testid="app-sidebar" id="mobile-navigation" ref={asideRef} onKeyDown={trapFocus}>
        <div className="sidebar-brand" data-testid="sidebar-brand">
          <img alt="TAGForce" className="sidebar-logo" src={logoWhite} />
          <span className="trademark">™</span>
          <button
            aria-label="Close navigation"
            className="sidebar-close"
            data-testid="mobile-nav-close"
            type="button"
            onClick={() => closeMobileNav()}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <nav className="sidebar-navigation" aria-label="Main navigation">
          {visibleGroups.map((group) => {
            const isHome = group.label === "Home";
            const expanded = isHome || group.label === activeGroup || !collapsed.has(group.label);
            const groupKey = group.label.toLowerCase();
            return (
              <section className={`sidebar-group ${expanded ? "is-expanded" : "is-collapsed"}`} data-testid={`nav-group-${groupKey}`} key={group.label}>
                {!isHome && (
                  <button
                    aria-controls={`nav-group-items-${groupKey}`}
                    aria-expanded={expanded}
                    className="sidebar-group-toggle"
                    data-testid={`nav-group-toggle-${groupKey}`}
                    disabled={group.label === activeGroup}
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                  >
                    <span className="sidebar-group-label">{group.label}</span>
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                )}
                <div className="sidebar-group-items" id={`nav-group-items-${groupKey}`} hidden={!expanded}>
                  {group.items.map((item) => {
                    const active = item.label.toLowerCase() === activeKey;
                    const Icon = item.icon;
                    return (
                      <button
                        aria-current={active ? "page" : undefined}
                        className={`sidebar-link ${active ? "sidebar-link-active" : ""}`}
                        data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                        key={item.label}
                        type="button"
                        onClick={() => go(item.path)}
                      >
                        <Icon size={17} strokeWidth={1.75} aria-hidden="true" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </nav>
        <p className="sidebar-footer">A product of The Allicock Group</p>
      </aside>
    </>
  );
}
